import React, { useState, useEffect, useCallback, useRef } from "react";
import { Spinner } from "react-bootstrap";
import { ArrowLeft, Search, User, SlidersHorizontal, ArrowUpDown, ChevronDown, X } from "lucide-react";
import CustomPagination from "../common/CustomPagination.jsx";
import { DateRangePicker } from "react-date-range";
import moment from "moment";
import SearchWithAPI from "../common/searchbox/searchbox.js";
import { getHandoverHistoryDetailsApi } from "./handover-api";
import calendarIcon from "../../Assets/svg/calendar.svg";
import { ReactComponent as CalendarPurpleIcon } from "../../Assets/svg/calendar-purple.svg";

// Helper to parse user string like "Ragu Ram <ragurr@gmail.com>" or separate email
const parseUserString = (userVal, emailVal) => {
  if (!userVal && !emailVal) {
    return { name: "-", email: "" };
  }
  let str = userVal ? String(userVal).trim() : "";
  let extractedEmail = emailVal ? String(emailVal).trim() : "";
  let extractedName = str;

  // Extract from "Name <email@domain.com>" pattern
  const angleMatch = str.match(/^(.*?)\s*<([^>]+)>/);
  if (angleMatch) {
    extractedName = angleMatch[1].trim() || angleMatch[2].split("@")[0];
    if (!extractedEmail) extractedEmail = angleMatch[2].trim();
  } else if (str.includes("@")) {
    const parts = str.split(/\s+/);
    const emailPart = parts.find((p) => p.includes("@"));
    if (emailPart) {
      if (!extractedEmail) extractedEmail = emailPart.replace(/[<>]/g, "");
      extractedName = parts.filter((p) => !p.includes("@")).join(" ").replace(/[<>]/g, "").trim() || extractedEmail.split("@")[0];
    }
  }

  // Remove any remaining angle brackets
  extractedName = extractedName.replace(/[<>]/g, "").trim() || "-";
  extractedEmail = extractedEmail.replace(/[<>]/g, "").trim();

  return { name: extractedName, email: extractedEmail };
};

// Helper for user initials (e.g. "Ragu Ram" -> "RR", "admin" -> "AD")
const getInitials = (name) => {
  if (!name || name === "-") return "";
  const cleanName = name.replace(/[^a-zA-Z0-9\s]/g, "").trim();
  const parts = cleanName.split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + (parts[parts.length - 1][0] || "")).toUpperCase();
};

const renderStatusBadge = (status) => {
  const st = (status || "").toLowerCase();
  let cssClass = "open";
  if (st.includes("progress")) cssClass = "in-progress";
  else if (st.includes("hold")) cssClass = "hold";
  else if (st.includes("closed")) cssClass = "closed";
  else if (st.includes("completed")) cssClass = "completed";

  return (
    <span className={`status-badge ${cssClass}`}>
      <span className="status-dot"></span>
      {status || "Open"}
    </span>
  );
};

const HandoverHistoryView = ({ selectedHistoryLog, onBack, recordsPerPage: initialRecordsPerPage = 10 }) => {
  const [recordsPerPage, setRecordsPerPage] = useState(initialRecordsPerPage);
  const [historyDetailLoading, setHistoryDetailLoading] = useState(false);
  const [historyDetailTickets, setHistoryDetailTickets] = useState([]);
  const [historyDetailTotalRecords, setHistoryDetailTotalRecords] = useState(0);
  const [historyDetailSearch, setHistoryDetailSearch] = useState("");
  const [historyDetailPage, setHistoryDetailPage] = useState(1);
  const [historyDetailInfo, setHistoryDetailInfo] = useState(null);
  const [historyStatusFilter, setHistoryStatusFilter] = useState("All");

  // Date Pickers State
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const [dateRangeState, setDateRangeState] = useState([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);

  const [expStartDate, setExpStartDate] = useState("");
  const [expEndDate, setExpEndDate] = useState("");
  const [showExpPicker, setShowExpPicker] = useState(false);
  const [expDateRangeState, setExpDateRangeState] = useState([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);

  // Filter Drawer State
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedProjectOptions, setSelectedProjectOptions] = useState([]);
  const [selectedTeamOptions, setSelectedTeamOptions] = useState([]);

  const datePickerRef = useRef(null);
  const expDatePickerRef = useRef(null);
  const historyStatusTabRefs = useRef({});
  const [historyStatusPillStyle, setHistoryStatusPillStyle] = useState({ left: 4, width: 0, height: 0 });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
      if (expDatePickerRef.current && !expDatePickerRef.current.contains(event.target)) {
        setShowExpPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const el = historyStatusTabRefs.current[historyStatusFilter];
    if (el) {
      setHistoryStatusPillStyle({
        left: el.offsetLeft,
        width: el.offsetWidth,
        height: el.offsetHeight,
      });
    }
  }, [historyStatusFilter, historyDetailTotalRecords]);

  const getFilterCount = () => {
    let count = 0;
    if (startDate || endDate) count++;
    if (expStartDate || expEndDate) count++;
    if (selectedProjectOptions && selectedProjectOptions.length > 0) count++;
    if (selectedTeamOptions && selectedTeamOptions.length > 0) count++;
    return count;
  };

  const fetchHistoryDetails = useCallback(async () => {
    if (!selectedHistoryLog?.id) return;
    setHistoryDetailLoading(true);
    try {
      const skip = (historyDetailPage - 1) * recordsPerPage;
      const params = {
        status: historyStatusFilter === "All" ? ["All"] : [historyStatusFilter],
        search: historyDetailSearch,
        limit: recordsPerPage,
        skip: skip,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        exp_start_date: expStartDate || undefined,
        exp_end_date: expEndDate || undefined,
      };
      const res = await getHandoverHistoryDetailsApi(selectedHistoryLog.id, params);
      if (res?.data?.success) {
        const data = res.data.data || res.data.details || res.data;
        setHistoryDetailInfo(data);
        setHistoryDetailTickets(data.tickets || res.data.tickets || []);
        setHistoryDetailTotalRecords(data.total_rec || res.data.total_rec || (data.tickets ? data.tickets.length : 0));
      } else {
        setHistoryDetailTickets(selectedHistoryLog.tickets || []);
        setHistoryDetailTotalRecords(selectedHistoryLog.ticket_count || (selectedHistoryLog.tickets ? selectedHistoryLog.tickets.length : 0));
      }
    } catch (err) {
      console.error("Error fetching handover history details:", err);
      setHistoryDetailTickets(selectedHistoryLog.tickets || []);
    } finally {
      setHistoryDetailLoading(false);
    }
  }, [
    selectedHistoryLog,
    historyStatusFilter,
    historyDetailSearch,
    historyDetailPage,
    recordsPerPage,
    startDate,
    endDate,
    expStartDate,
    expEndDate,
  ]);

  useEffect(() => {
    fetchHistoryDetails();
  }, [fetchHistoryDetails]);

  // Parse From User & To User
  const fromUserObj = parseUserString(
    historyDetailInfo?.from_user || selectedHistoryLog?.from_user,
    historyDetailInfo?.from_user_email || selectedHistoryLog?.from_user_email
  );

  const toUserObj = parseUserString(
    historyDetailInfo?.to_user || selectedHistoryLog?.to_user,
    historyDetailInfo?.to_user_email || selectedHistoryLog?.to_user_email
  );

  // Parse Roles
  const assignedObj = parseUserString(
    historyDetailInfo?.assigned_user || selectedHistoryLog?.assigned_user || toUserObj.name
  );

  const reporterObj = parseUserString(
    historyDetailInfo?.reporter_user || selectedHistoryLog?.reporter_user || fromUserObj.name
  );

  const createdObj = parseUserString(
    historyDetailInfo?.created_user || selectedHistoryLog?.created_user || historyDetailInfo?.admin_user || selectedHistoryLog?.admin_user
  );

  return (
    <div className="history-detail-wrapper">
      
      <div className="history-detail-header-card">
        <button
          type="button"
          className="history-back-btn"
          onClick={onBack}
          title="Back to History List"
        >
          <ArrowLeft size={18} color="#212129" />
        </button>

        
        <div className="history-user-pill">
          <div className="history-avatar from">
            {getInitials(fromUserObj.name)}
          </div>
          <div className="history-user-details">
            <span className="history-user-name">{fromUserObj.name}</span>
            {fromUserObj.email && (
              <span className="history-user-email">{fromUserObj.email}</span>
            )}
          </div>
        </div>

        
        <div className="history-connector-wrapper">
          <div className="history-connector-line" />
          <div className="history-date-badge">
            {historyDetailInfo?.date_time || selectedHistoryLog?.date_time || "-"}
          </div>
        </div>

        
        <div className="history-user-pill">
          <div className="history-avatar to">
            {getInitials(toUserObj.name)}
          </div>
          <div className="history-user-details">
            <span className="history-user-name">{toUserObj.name}</span>
            {toUserObj.email && (
              <span className="history-user-email">{toUserObj.email}</span>
            )}
          </div>
        </div>
      </div>

      
      <div className="history-details-card">
        
        <div className="history-summary-roles-box">
          
          <div className="history-summary-pills-col">
            <div className="history-pill full-width">
              <span className="pill-label">Total Tickets</span>
              <span className="pill-val">
                {historyDetailInfo?.ticket_count ?? selectedHistoryLog?.ticket_count ?? historyDetailTotalRecords}
              </span>
            </div>
            <div className="history-pill-row">
              <div className="history-pill">
                <span className="pill-label">Open</span>
                <span className="pill-val">{historyDetailInfo?.open_count ?? selectedHistoryLog?.open_count ?? 0}</span>
              </div>
              <div className="history-pill">
                <span className="pill-label">Hold</span>
                <span className="pill-val">{historyDetailInfo?.hold_count ?? selectedHistoryLog?.hold_count ?? 0}</span>
              </div>
            </div>
            <div className="history-pill-row">
              <div className="history-pill">
                <span className="pill-label">Closed</span>
                <span className="pill-val">{historyDetailInfo?.closed_count ?? selectedHistoryLog?.closed_count ?? 0}</span>
              </div>
              <div className="history-pill">
                <span className="pill-label">Completed</span>
                <span className="pill-val">{historyDetailInfo?.completed_count ?? selectedHistoryLog?.completed_count ?? 0}</span>
              </div>
            </div>
          </div>

          
          <div className="history-roles-col">
            <div className="history-role-row">
              <span className="role-lbl">ASSIGNED</span>
              <span className="role-colon">:</span>
              <span className="role-val" title={assignedObj.name}>
                <User size={14} className="me-1 text-muted flex-shrink-0" />
                <span className="role-name-text">{assignedObj.name}</span>
              </span>
            </div>
            <div className="history-role-row">
              <span className="role-lbl">REPORTER</span>
              <span className="role-colon">:</span>
              <span className="role-val" title={reporterObj.name}>
                <User size={14} className="me-1 text-muted flex-shrink-0" />
                <span className="role-name-text">{reporterObj.name}</span>
              </span>
            </div>
            <div className="history-role-row">
              <span className="role-lbl">CREATED BY</span>
              <span className="role-colon">:</span>
              <span className="role-val" title={createdObj.name}>
                <User size={14} className="me-1 text-muted flex-shrink-0" />
                <span className="role-name-text">{createdObj.name}</span>
              </span>
            </div>
          </div>
        </div>

        <div className="history-remarks-block">
          <span className="remarks-heading">REMARKS</span>
          <p className="remarks-body">
            {historyDetailInfo?.remarks || selectedHistoryLog?.remarks || "-"}
          </p>
        </div>
      </div>

      <div className="handover-table-card">
        <div className="handover-table-toolbar">
          <div className="status-pill-group">
            <div
              className="status-pill-indicator"
              style={{
                left: `${historyStatusPillStyle.left}px`,
                width: `${historyStatusPillStyle.width}px`,
                height: `${historyStatusPillStyle.height}px`,
              }}
            />
            {["All", "Open", "Hold", "Closed", "Completed"].map((st) => (
              <button
                key={st}
                ref={(el) => (historyStatusTabRefs.current[st] = el)}
                className={`status-pill-btn ${historyStatusFilter === st ? "active" : ""}`}
                onClick={() => {
                  setHistoryStatusFilter(st);
                  setHistoryDetailPage(1);
                }}
              >
                {historyStatusFilter === st ? `${st} (${historyDetailTotalRecords})` : st}
              </button>
            ))}
          </div>

          
          <div className="table-toolbar-right">
            
            <div ref={datePickerRef} style={{ height: "40px", flexShrink: 0, position: "relative" }}>
              <div
                onClick={() => {
                  setShowPicker(!showPicker);
                  setShowExpPicker(false);
                }}
                className="date-filter-btn"
                style={{
                  border: startDate || endDate ? "1px solid #6929E2" : "1px solid #E5E5E5",
                  cursor: "pointer",
                  height: "40px",
                  borderRadius: "100px",
                  padding: "0 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  backgroundColor: "#FFFFFF",
                }}
              >
                {startDate || endDate ? (
                  <CalendarPurpleIcon style={{ width: "16px", height: "16px" }} />
                ) : (
                  <img src={calendarIcon} alt="calendar" style={{ width: "16px", height: "16px" }} />
                )}
                <span
                  style={{
                    fontFamily: "Satoshi",
                    fontSize: "14px",
                    fontWeight: startDate || endDate ? "600" : "500",
                    color: startDate || endDate ? "#6929E2" : "#212129",
                  }}
                >
                  {startDate && endDate ? `${moment(startDate).format("DD MMM")} - ${moment(endDate).format("DD MMM")}` : "Date Range"}
                </span>
                {(startDate || endDate) && (
                  <X
                    size={14}
                    color="#6929e2"
                    style={{ cursor: "pointer", marginLeft: "4px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setStartDate("");
                      setEndDate("");
                      setDateRangeState([{ startDate: new Date(), endDate: new Date(), key: "selection" }]);
                    }}
                  />
                )}
              </div>
              {showPicker && (
                <div
                  style={{
                    position: "absolute",
                    top: "48px",
                    right: "0",
                    zIndex: 300,
                    background: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "12px",
                    boxShadow: "0px 4px 24px rgba(0,0,0,0.15)",
                    padding: "8px",
                  }}
                >
                  <DateRangePicker
                    ranges={dateRangeState}
                    onChange={(item) => {
                      setStartDate(moment(item.selection.startDate).format("YYYY-MM-DD"));
                      setEndDate(moment(item.selection.endDate).format("YYYY-MM-DD"));
                      setDateRangeState([item.selection]);
                    }}
                    showSelectionPreview={true}
                    moveRangeOnFirstSelection={false}
                    months={1}
                    direction="horizontal"
                  />
                </div>
              )}
            </div>

            
            <div ref={expDatePickerRef} style={{ height: "40px", flexShrink: 0, position: "relative" }}>
              <div
                onClick={() => {
                  setShowExpPicker(!showExpPicker);
                  setShowPicker(false);
                }}
                className="date-filter-btn"
                style={{
                  border: expStartDate || expEndDate ? "1px solid #6929E2" : "1px solid #E5E5E5",
                  cursor: "pointer",
                  height: "40px",
                  borderRadius: "100px",
                  padding: "0 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  backgroundColor: "#FFFFFF",
                }}
              >
                {expStartDate || expEndDate ? (
                  <CalendarPurpleIcon style={{ width: "16px", height: "16px" }} />
                ) : (
                  <img src={calendarIcon} alt="calendar" style={{ width: "16px", height: "16px" }} />
                )}
                <span
                  style={{
                    fontFamily: "Satoshi",
                    fontSize: "14px",
                    fontWeight: expStartDate || expEndDate ? "600" : "500",
                    color: expStartDate || expEndDate ? "#6929E2" : "#212129",
                  }}
                >
                  {expStartDate && expEndDate
                    ? `${moment(expStartDate).format("DD MMM")} - ${moment(expEndDate).format("DD MMM")}`
                    : "Exp. date"}
                </span>
                {(expStartDate || expEndDate) && (
                  <X
                    size={14}
                    color="#6929e2"
                    style={{ cursor: "pointer", marginLeft: "4px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpStartDate("");
                      setExpEndDate("");
                    }}
                  />
                )}
              </div>
              {showExpPicker && (
                <div
                  style={{
                    position: "absolute",
                    top: "48px",
                    right: "0",
                    zIndex: 300,
                    background: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "12px",
                    boxShadow: "0px 4px 24px rgba(0,0,0,0.15)",
                    padding: "8px",
                  }}
                >
                  <DateRangePicker
                    ranges={expDateRangeState}
                    onChange={(item) => {
                      setExpStartDate(moment(item.selection.startDate).format("YYYY-MM-DD"));
                      setExpEndDate(moment(item.selection.endDate).format("YYYY-MM-DD"));
                      setExpDateRangeState([item.selection]);
                    }}
                    showSelectionPreview={true}
                    moveRangeOnFirstSelection={false}
                    months={1}
                    direction="horizontal"
                  />
                </div>
              )}
            </div>

            
            <div className="search-input-wrapper" style={{ width: "200px", minWidth: "160px", position: "relative" }}>
              <Search className="search-icon" size={15} color="#8790a1" />
              <input
                type="text"
                placeholder="Search tasks..."
                style={{
                  width: "100%",
                  height: "40px",
                  borderRadius: "100px",
                  border: "1px solid #E5E5E5",
                  paddingLeft: "40px",
                  fontSize: "14px",
                  color: "#212129",
                  outline: "none",
                }}
                value={historyDetailSearch}
                onChange={(e) => {
                  setHistoryDetailSearch(e.target.value);
                  setHistoryDetailPage(1);
                }}
              />
            </div>

            
            <div style={{ position: "relative", flexShrink: 0 }}>
              <div
                onClick={() => setIsFilterOpen(true)}
                style={{
                  height: "40px",
                  borderRadius: "100px",
                  border: getFilterCount() > 0 ? "1px solid #6929E2" : "1px solid #E5E5E5",
                  backgroundColor: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  padding: "0 14px",
                  cursor: "pointer",
                  gap: "8px",
                }}
              >
                <SlidersHorizontal size={16} color={getFilterCount() > 0 ? "#6929e2" : "#3c4257"} />
                {getFilterCount() > 0 && (
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      backgroundColor: "#F3EDFF",
                      color: "#6929E2",
                      fontSize: "13px",
                      fontWeight: "700",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: "Satoshi",
                    }}
                  >
                    {getFilterCount()}
                  </div>
                )}
              </div>
              {getFilterCount() > 0 && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setStartDate("");
                    setEndDate("");
                    setExpStartDate("");
                    setExpEndDate("");
                    setSelectedProjectOptions([]);
                    setSelectedTeamOptions([]);
                    setDateRangeState([{ startDate: new Date(), endDate: new Date(), key: "selection" }]);
                    setExpDateRangeState([{ startDate: new Date(), endDate: new Date(), key: "selection" }]);
                  }}
                  style={{
                    position: "absolute",
                    top: "-6px",
                    right: "-4px",
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    backgroundColor: "#FF4444",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    border: "2px solid #FFFFFF",
                    zIndex: 10,
                  }}
                  title="Clear filter"
                >
                  <X size={10} color="#FFFFFF" strokeWidth={3} />
                </div>
              )}
            </div>

            
            <div style={{ position: "relative", flexShrink: 0 }}>
              <div
                style={{
                  height: "40px",
                  borderRadius: "100px",
                  border: "1px solid #E5E5E5",
                  backgroundColor: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  padding: "0 16px",
                  cursor: "pointer",
                  gap: "8px",
                  whiteSpace: "nowrap",
                }}
              >
                <ArrowUpDown size={15} color="#6929e2" />
                <span style={{ fontFamily: "Satoshi", fontSize: "14px", fontWeight: "700", color: "#212129" }}>
                  Default <span style={{ fontWeight: "400", color: "#8790A1" }}>(Created)</span>
                </span>
                <ChevronDown size={14} color="#3c4257" />
              </div>
            </div>
          </div>
        </div>

        
        <div className="handover-table-responsive">
          <table className="handover-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Task Title</th>
                <th>Project</th>
                <th>Module</th>
                <th>Assigned to</th>
                <th>Reporter</th>
                <th>Expected date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {historyDetailLoading ? (
                <tr>
                  <td colSpan="8" className="text-center py-5">
                    <div className="d-flex flex-column align-items-center justify-content-center gap-3 py-3">
                      <Spinner animation="border" style={{ color: "#6929e2", width: "2.2rem", height: "2.2rem", borderWidth: "3px" }} />
                      <span style={{ fontSize: "14px", color: "#697386", fontWeight: "500" }}>Loading history details...</span>
                    </div>
                  </td>
                </tr>
              ) : historyDetailTickets.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-4 text-muted">
                    No tickets found for this handover record.
                  </td>
                </tr>
              ) : (
                historyDetailTickets.map((t) => (
                  <tr key={t.id}>
                    <td style={{ fontWeight: "600", color: "#1a1f36" }}>{t.code || t.ticket_code || "-"}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span>{t.title || t.ticket_title || "-"}</span>
                      </div>
                    </td>
                    <td>{t.project || t.project_name || "-"}</td>
                    <td>{t.module || t.module_name || "-"}</td>
                    <td>{t.assigned_to || t.assigned_user || "-"}</td>
                    <td>{t.reporter || t.reporter_name || "-"}</td>
                    <td>{t.expected_date || t.date || "-"}</td>
                    <td>{renderStatusBadge(t.status)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        
        <CustomPagination
          currentPage={historyDetailPage}
          totalRecords={historyDetailTotalRecords}
          recordsPerPage={recordsPerPage}
          onPageChange={setHistoryDetailPage}
          labelText="Records per page "
        />
      </div>

      
      {isFilterOpen && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            zIndex: 2000,
          }}
          onClick={() => setIsFilterOpen(false)}
        >
          <div
            className="filter-drawer-panel"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "380px",
              height: "100%",
              backgroundColor: "#F6F6F6",
              boxShadow: "-4px 0 24px rgba(0, 0, 0, 0.15)",
              zIndex: 2001,
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              overflowY: "auto",
            }}
          >
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span style={{ fontSize: "18px", fontWeight: "700", color: "#212129" }}>Filters</span>
                <div
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid #EBEBEB",
                    borderRadius: "100px",
                    width: "36px",
                    height: "36px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => setIsFilterOpen(false)}
                >
                  <X size={14} color="#212129" />
                </div>
              </div>

              <div style={{ borderRadius: "16px", padding: "16px", background: "#FFFFFF" }} className="d-flex flex-column gap-3">
                <div>
                  <label style={{ fontSize: "13px", fontWeight: "600", color: "#172b4d", marginBottom: "6px", display: "block" }}>
                    Project
                  </label>
                  <SearchWithAPI
                    apiUrl="get_project"
                    placeholder="Select Project"
                    selectedValue={selectedProjectOptions && selectedProjectOptions.length > 0 ? selectedProjectOptions[0].label : ""}
                    selectedOption={selectedProjectOptions && selectedProjectOptions.length > 0 ? selectedProjectOptions[0] : null}
                    onDropdownChange={(opt) => setSelectedProjectOptions(opt ? [opt] : [])}
                  />
                </div>

                <div>
                  <label style={{ fontSize: "13px", fontWeight: "600", color: "#172b4d", marginBottom: "6px", display: "block" }}>
                    Team
                  </label>
                  <SearchWithAPI
                    apiUrl="get_team"
                    placeholder="Select Team"
                    selectedValue={selectedTeamOptions && selectedTeamOptions.length > 0 ? selectedTeamOptions[0].label : ""}
                    selectedOption={selectedTeamOptions && selectedTeamOptions.length > 0 ? selectedTeamOptions[0] : null}
                    onDropdownChange={(opt) => setSelectedTeamOptions(opt ? [opt] : [])}
                  />
                </div>

                <div>
                  <label style={{ fontSize: "13px", fontWeight: "600", color: "#172b4d", marginBottom: "6px", display: "block" }}>
                    Created Date Range
                  </label>
                  <div className="d-flex gap-2">
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={{ borderRadius: "8px", height: "38px" }}
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={{ borderRadius: "8px", height: "38px" }}
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: "13px", fontWeight: "600", color: "#172b4d", marginBottom: "6px", display: "block" }}>
                    Expected Date Range
                  </label>
                  <div className="d-flex gap-2">
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={{ borderRadius: "8px", height: "38px" }}
                      value={expStartDate}
                      onChange={(e) => setExpStartDate(e.target.value)}
                    />
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={{ borderRadius: "8px", height: "38px" }}
                      value={expEndDate}
                      onChange={(e) => setExpEndDate(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-between gap-3 pt-3">
              <button
                type="button"
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "100px",
                  backgroundColor: "#f0f0f0",
                  color: "#21272C",
                  fontWeight: "700",
                  cursor: "pointer",
                  fontSize: "13px",
                  flex: 1,
                }}
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                  setExpStartDate("");
                  setExpEndDate("");
                  setSelectedProjectOptions([]);
                  setSelectedTeamOptions([]);
                }}
              >
                Reset
              </button>
              <button
                type="button"
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "100px",
                  backgroundColor: "#6929E2",
                  color: "#ffffff",
                  fontWeight: "700",
                  cursor: "pointer",
                  fontSize: "13px",
                  flex: 1,
                }}
                onClick={() => {
                  setIsFilterOpen(false);
                }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HandoverHistoryView;
